import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import styled from 'styled-components'
import { rem } from 'polished'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Hr from 'components/Common/Hr'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useOrder from 'hooks/Order/useOrder'
import { randomIntegerBetween } from 'lib/maths/mathUtils'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import useItemTypeLabels from 'hooks/useItemTypeLabels'
import useOrderFlightName from 'luxPlus/hooks/useOrderFlightName'

const LineComponent = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr ${rem(184)};
    gap: ${rem(16)};
    align-items: center;
  }
`

interface Props {
  item: App.RedeemedMembershipBenefit
  hideDivider?: boolean
}

export function useHumanReadableBenefitTypeLabel(): Record<string, string> {
  const intl = useIntl()
  return useMemo(() => ({
    hotel_inclusions: intl.formatMessage({
      id: 'b_gBfBKWjCjXncpcg_Nf',
      defaultMessage: 'Bonus inclusions',
      description: 'Benefit type label for hotel inclusions on the hotel or room booking page',
    }),
    cruise_inclusions: intl.formatMessage({
      id: 'OU7QmHF5wnPJUHrgAOke',
      defaultMessage: 'Bonus inclusions',
      description: 'Benefit type label for cruise inclusions in the cruise section',
    }),
    discounted_tours: intl.formatMessage({
      id: 'oWSHt9bteOtR7uDaQB2a',
      defaultMessage: 'Discounted price',
      description: 'Benefit type label for discounted tours on the tour offer detail page',
    }),
    discounted_experiences: intl.formatMessage({
      id: 'X31SQ5WS2vylFUEtHkhy',
      defaultMessage: 'Discounted experiences',
      description: 'Benefit type label for discounted experiences in the LuxPlus membership section',
    }),
    discounted_flash: intl.formatMessage({
      id: 'PKnRenYz2cyU_ej8VPzB',
      defaultMessage: 'Discounted price',
      description: 'Benefit type label for discounted flash offers in the LuxPlus membership section',
    }),
    discounted_flights: intl.formatMessage({
      id: 'omx6BS6ZZIru3PjndYDh',
      defaultMessage: 'Discounted price',
      description: 'Benefit type label for discounted flights in the LuxPlus membership section',
    }),
    exclusive_offers: intl.formatMessage({
      id: 'HicIRXcTAThQ4w9uQiSI',
      defaultMessage: 'Exclusive offer',
      description: 'Benefit type label for exclusive offers on the offer listing or detail page',
    }),
    early_access: intl.formatMessage({
      id: 'kwRg-mwzR7kQ4cuhNgoO',
      defaultMessage: 'Early access',
      description: 'Benefit type label for early access offers on the offer page',
    }),
    daily_breakfast: intl.formatMessage({
      id: 'DhxHjVfhskCMmV71r8xi',
      defaultMessage: 'Daily breakfast',
      description: 'Benefit type label for daily breakfast inclusion in the LuxPlus membership section',
    }),
    hotel_credit: intl.formatMessage({
      id: 'JZjYdKUsMHLwqMjtSwEh',
      defaultMessage: 'Hotel credit',
      description: 'Benefit type label for hotel credit inclusion in the LuxPlus membership section',
    }),
    discounted_insurance: intl.formatMessage({
      id: 'omRgRf_ZQIBrC6GNjptQ',
      defaultMessage: 'Discounted insurance',
      description: 'Benefit type label for discounted insurance in the insurance flow',
    }),
    other: intl.formatMessage({
      id: 'vGb5vAk1F3RKiMMBZmz1',
      defaultMessage: 'Bonus inclusions',
      description: 'Benefit type label for other/miscellaneous benefits',
    }),
    promo: intl.formatMessage({
      id: 'zKMXeoOlscagw5FN6Ik7',
      defaultMessage: 'Promo code discount',
      description: 'Benefit type label for promo code discount applied to an order',
    }),
    voucher: intl.formatMessage({
      id: 'rQOh0cuWjadFmRHQkcUO',
      defaultMessage: 'Voucher',
      description: 'Benefit type label for voucher savings for promotional codes or vouchers',
    }),
  }), [intl])
}

function LuxPlusViewActivityLine({ item, hideDivider = false }: Props) {
  const [order, fetching] = useOrder(item?.orderId)
  const humanReadableBenefitTypeLabel = useHumanReadableBenefitTypeLabel()
  const itemTypeLabels = useItemTypeLabels()

  const orderTourItem = useMemo(() => {
    return order?.tourItems?.find(i => i.id === item?.itemId)
  }, [item?.itemId, order?.tourItems])

  const orderItem = useMemo(() => {
    return order?.items?.find(i => i.id === item?.itemId)
  }, [item?.itemId, order?.items])

  const orderAddonsItem = useMemo(() => {
    return order?.addonItems?.find(i => i.id === item?.itemId)
  }, [item?.itemId, order?.addonItems])

  const orderBedbankItem = useMemo(() => {
    return order?.bedbankItems?.find(i => i.id === item?.itemId)
  }, [item?.itemId, order?.bedbankItems])

  const orderCruiseItem = useMemo(() => {
    return order?.cruiseItems?.find(i => i.id === item?.itemId)
  }, [item?.itemId, order?.cruiseItems])

  const orderInsuranceItem = useMemo(() => {
    const insuranceItem = order?.insuranceItems?.find(i => i.id === item?.itemId)
    const bookingProtectionItem = order?.bookingProtectionItems?.find(i => i.id === item?.itemId)
    return insuranceItem ?? bookingProtectionItem
  }, [item?.itemId, order?.bookingProtectionItems, order?.insuranceItems])

  const orderExperienceItem = useMemo(() => {
    return order?.experienceItems?.find(i => i.id === item?.itemId)
  }, [item?.itemId, order?.experienceItems])

  const orderFlightItem = useMemo(() => {
    return order?.flightItems?.find(i => i.id === item?.itemId)
  }, [item?.itemId, order?.flightItems])

  // Match the My Escapes flight tile title (full airport names + countries) instead of
  // IATA codes. Only fetch flight details once we know the item is a flight.
  const isFlightItem = Boolean(orderFlightItem)
  const { flightName, fetching: flightFetching } = useOrderFlightName(isFlightItem ? order : undefined)

  const itemName = orderTourItem?.tour.title ?? orderItem?.offer.property?.name ?? orderInsuranceItem?.productName ?? orderAddonsItem?.name ?? orderBedbankItem?.offer.property?.name ?? orderCruiseItem?.offerName ?? orderExperienceItem?.title ?? flightName ?? (item.description || itemTypeLabels[item.itemType])

  const benefitType = humanReadableBenefitTypeLabel[item?.subtype ?? ''] ?? humanReadableBenefitTypeLabel[item?.type] ?? ''
  const resolving = fetching || (isFlightItem && flightFetching)
  if (!itemName && !resolving) return null
  return <div key={item.id}>
    <LineComponent>
      <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="start" tabletGap={16}>
        <BodyText colour="light-foreground-alt" wrap="no-wrap" variant="medium"><FormatDateTime value={item.updatedAt} format="dmy-casual" /></BodyText>
        {resolving && <TextLoadingBox width={`${randomIntegerBetween(6, 10) * 2}ch`} />}
        {!resolving && <BodyText variant="medium" weight="bold">{itemName}</BodyText>}
      </Group>
      <BodyText align="start" tabletAlign="end" weight="bold" colour="lux-plus" variant="medium">
        {item.unit === 'currency' && item.adjustment > 0 && <FormattedMessage
          id="NA2gidSGqMaARua1IU4Z"
          defaultMessage="{amount} {benefitType}"
          description="LuxPlus activity line showing currency adjustment amount and benefit type"
          values={{ amount: <FormatCurrency format="rounded" value={item.adjustment} />, benefitType }}
        />}
        {item.unit === 'units' && item.adjustment > 1 && <FormattedMessage
          id="P3Dc6vllqqUwxzFRZWYG"
          defaultMessage="{count}x {benefitType}"
          description="LuxPlus activity line showing unit multiplier and benefit type"
          values={{ count: <FormatDecimal value={item.adjustment} />, benefitType }}
        />}
        {!(item.unit === 'currency' && item.adjustment > 0) && !(item.unit === 'units' && item.adjustment > 1) && benefitType}
      </BodyText>
    </LineComponent>
    {!hideDivider && <Hr size="xs" />}
  </div>
}

export default LuxPlusViewActivityLine
